import { useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  Sparkles, 
  Loader2, 
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  Lightbulb,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const AIProjectInsights = ({ projectId }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInsights = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/ai/project-insights', {
        project_id: projectId
      });
      setInsights(response.data);
    } catch (error) {
      console.error('Failed to get AI insights:', error);
      setError('Failed to generate insights. Please try again.');
      toast.error('Failed to get AI insights');
    } finally {
      setLoading(false);
    }
  };

  if (!insights && !loading && !error) {
    return (
      <Card className="border-dashed border-2 border-slate-200 bg-slate-50/50" data-testid="ai-insights-card">
        <CardContent className="py-8 text-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#235894] to-[#26B8A7] flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <h3 className="font-semibold text-slate-900 mb-2">AI Project Insights</h3>
          <p className="text-sm text-slate-500 mb-4 max-w-sm mx-auto">
            Get AI-powered recommendations and risk assessment based on your project data
          </p>
          <Button 
            onClick={fetchInsights}
            className="bg-gradient-to-r from-[#235894] to-[#26B8A7] hover:from-[#1E4B80] hover:to-[#1fa393]"
            data-testid="get-ai-insights-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Generate Insights
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card data-testid="ai-insights-loading">
        <CardContent className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#235894] mx-auto mb-4" />
          <p className="text-slate-500">Analyzing project data...</p>
          <p className="text-xs text-slate-400 mt-1">This may take a few seconds</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50/50">
        <CardContent className="py-8 text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <Button variant="outline" onClick={fetchInsights}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="ai-insights-results">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#235894] to-[#26B8A7] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            AI Project Insights
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={fetchInsights}
            className="text-slate-500"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Main Insights */}
        <div className="p-4 bg-slate-50 rounded-lg">
          <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            Summary
          </h4>
          <p className="text-sm text-slate-700 leading-relaxed">
            {insights.insights}
          </p>
        </div>

        {/* Risk Assessment */}
        <div className={cn(
          "p-4 rounded-lg",
          insights.risk_assessment?.toLowerCase().includes('high') 
            ? "bg-red-50 border border-red-100"
            : insights.risk_assessment?.toLowerCase().includes('medium')
            ? "bg-amber-50 border border-amber-100"
            : "bg-emerald-50 border border-emerald-100"
        )}>
          <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
            <AlertTriangle className={cn(
              "w-4 h-4",
              insights.risk_assessment?.toLowerCase().includes('high')
                ? "text-red-500"
                : insights.risk_assessment?.toLowerCase().includes('medium')
                ? "text-amber-500"
                : "text-emerald-500"
            )} />
            Risk Assessment
          </h4>
          <p className="text-sm text-slate-700 leading-relaxed">
            {insights.risk_assessment}
          </p>
        </div>

        {/* Recommendations */}
        {insights.recommendations?.length > 0 && (
          <div>
            <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-blue-500" />
              Recommendations
            </h4>
            <ul className="space-y-2">
              {insights.recommendations.map((rec, index) => (
                <li 
                  key={index}
                  className="flex items-start gap-2 text-sm text-slate-700"
                >
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0 text-xs font-medium">
                    {index + 1}
                  </span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Next Actions */}
        {insights.next_actions?.length > 0 && (
          <div className="p-4 bg-gradient-to-r from-[#235894]/5 to-[#26B8A7]/5 rounded-lg border border-[#235894]/10">
            <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
              <ArrowRight className="w-4 h-4 text-[#26B8A7]" />
              Suggested Next Actions
            </h4>
            <ul className="space-y-2">
              {insights.next_actions.map((action, index) => (
                <li 
                  key={index}
                  className="flex items-start gap-2 text-sm text-slate-700"
                >
                  <ArrowRight className="w-4 h-4 text-[#26B8A7] flex-shrink-0 mt-0.5" />
                  <span>{action}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIProjectInsights;
